import React from 'react'
import { TextField, TextFieldProps, InputAdornment } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Extinde TextFieldProps cu proprietăți custom
export interface StandardInputProps extends Omit<TextFieldProps, 'variant'> {
  variant?: 'outlined' | 'filled'
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  helpText?: string
  error?: boolean
  errorText?: string
  loading?: boolean
}

/**
 * StandardInput - Input unificat pentru toată aplicația
 * 
 * Features:
 * - Height standardizat: 48px
 * - Styling consistent cu design system
 * - Support pentru iconițe
 * - States: normal, focus, error, disabled
 * - Help text și error text
 */
export const StandardInput: React.FC<StandardInputProps> = ({
  variant = 'outlined',
  startIcon,
  endIcon,
  helpText,
  error,
  errorText,
  loading,
  sx,
  InputProps,
  ...props
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  // Construim InputProps pentru iconițe
  const inputProps = {
    ...InputProps,
    ...(startIcon && {
      startAdornment: (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ),
    }),
    ...(endIcon && {
      endAdornment: (
        <InputAdornment position="end">
          {loading ? <span>⏳</span> : endIcon}
        </InputAdornment>
      ),
    }),
  }

  return (
    <TextField
      variant={variant}
      error={error}
      helperText={errorText || helpText}
      disabled={loading}
      InputProps={inputProps}
      sx={{
        width: '100%',
        
        // Input container styling
        '& .MuiOutlinedInput-root': {
          height: designSystem.components.input.height,
          borderRadius: designSystem.components.input.borderRadius,
          fontSize: designSystem.typography.variants.body1.fontSize,
          fontFamily: designSystem.typography.fontFamily,
          backgroundColor: colors.background.paper,
          
              // Input field padding
              '& input': {
                padding: `0 ${designSystem.components.input.padding.horizontal}px`,
                height: '100%',
                boxSizing: 'border-box',
                color: colors.text.primary,
                '&::placeholder': {
                  color: colors.text.disabled,
                  opacity: 1,
                  fontSize: designSystem.typography.variants.body1.fontSize,
                  fontWeight: 400,
                },
              },
          
          // Borders
          '& fieldset': {
            borderColor: colors.border.default,
            borderWidth: '1px',
            transition: 'all 0.2s ease-in-out',
          },
          
          // Hover state
          '&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) fieldset': {
            borderColor: colors.border.light,
          },
          
          // Focus state
          '&.Mui-focused fieldset': {
            borderColor: colors.border.focus,
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${colors.accent.primary}20`,
          },
          
          // Error state
          '&.Mui-error fieldset': {
            borderColor: designSystem.colors.status.error,
          },
          
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: designSystem.colors.status.error,
            boxShadow: `0 0 0 3px ${designSystem.colors.status.error}20`,
          },
          
          // Disabled state
          '&.Mui-disabled': {
            backgroundColor: colors.background.default,
            '& fieldset': {
              borderColor: colors.border.default,
              opacity: 0.5,
            },
            '& input': {
              color: colors.text.disabled,
              WebkitTextFillColor: colors.text.disabled,
            },
          },
        },
        
        // Label styling
        '& .MuiInputLabel-root': {
          fontSize: designSystem.typography.variants.body2.fontSize,
          fontWeight: 500,
          fontFamily: designSystem.typography.fontFamily,
          color: colors.text.secondary,
          transform: 'translate(16px, 14px) scale(1)',
          
          // Focused/filled label
          '&.Mui-focused, &.MuiFormLabel-filled': {
            color: colors.accent.primary,
            transform: 'translate(16px, -9px) scale(0.85)',
          },
          
          // Error label
          '&.Mui-error': {
            color: designSystem.colors.status.error,
          },
        },
        
        // Helper text styling
        '& .MuiFormHelperText-root': {
          fontSize: designSystem.typography.variants.caption.fontSize,
          fontFamily: designSystem.typography.fontFamily,
          marginLeft: '14px',
          marginTop: '6px',
          
          // Error helper text
          '&.Mui-error': {
            color: designSystem.colors.status.error,
          },
        },
        
        // Icon styling în InputAdornment
        '& .MuiInputAdornment-root': {
          color: colors.text.secondary,
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
          },
        },
        
        ...sx,
      }}
      {...props}
    />
  )
}

export default StandardInput
